import { observer } from 'mobx-react-lite'
import {
  type FiltersViewModel,
  type FilterViewModel,
} from '@/infra/FiltersViewModel'
import {
  AppliedFilterChip,
  AvailableFilterItem,
  AvailableFilters,
  FilterControls,
} from '@/components/FilterControls'
import { DateRangeFilter } from '@/infra/DateRangeFilter'
import { DateRangeFilterViewContainer } from '@/components/FilterControls/DateRange/DateRangeFilterViewContainer'
import { InfiniteSelectFilter } from '@/infra/InfiniteSelectFilter'
import { InfiniteSelectFilterViewContainer } from '@/components/FilterControls/InfiniteSelect/InfiniteSelectFilterViewContainer'
import { NumberRangeFilter } from '@/infra/NumberRangeFilter'
import { NumberRangeFilterViewContainer } from '@/components/FilterControls/NumberRange/NumberRangeFilterViewContainer'
import { SelectFilter } from '@/infra/SelectFilter'
import { SelectFilterViewContainer } from '@/components/FilterControls/Select/SelectFilterViewContainer'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import { SingleSelectFilterViewContainer } from '@/components/FilterControls/SingleSelect/SingleSelectFilterViewContainer'
import { FlagFilter } from '@/infra/FlagFilter'

interface FilterControlsContainerProps<
  TFilters extends Record<string, unknown>,
> {
  vm: FiltersViewModel<TFilters>
  active: boolean
  /**
   * Composes the test IDs of the filter editors.
   */
  editorTestId?: string
  testId?: string
}

/**
 * {@link FilterControls} for a {@link FiltersViewModel}.
 */
export const FilterControlsContainer = observer(
  function FilterControlsContainer<TFilters extends Record<string, unknown>>({
    vm,
    active,
    editorTestId,
    testId,
  }: FilterControlsContainerProps<TFilters>) {
    return (
      <FilterControls
        active={active}
        onClearAll={vm.clearExplicitFilters}
        appliedFilters={vm.explicitFilterVms.map((f) => (
          <AppliedFilterChipContainer
            key={f.key}
            filter={f}
            vm={vm}
            editorTestId={editorTestId}
          />
        ))}
        availableFilters={<AvailableFiltersContainer vm={vm} testId={testId} />}
        testId={testId}
      />
    )
  },
)

interface AppliedFilterChipContainerProps<
  TFilters extends Record<string, unknown>,
> {
  filter: FilterViewModel<string, unknown>
  vm: FiltersViewModel<TFilters>
  editorTestId?: string
}

const AppliedFilterChipContainer = observer(function AppliedFilterChipContainer<
  TFilters extends Record<string, unknown>,
>({ filter, vm, editorTestId }: AppliedFilterChipContainerProps<TFilters>) {
  return (
    <AppliedFilterChip
      label={filter.toString()}
      isEditing={filter.editing}
      onStartEditing={filter.startEditing}
      onStopEditing={filter.stopEditing}
      onRemove={() => vm.clearFilter(filter.key)}
    >
      <FilterEditorContainer filter={filter} testId={editorTestId} />
    </AppliedFilterChip>
  )
})

interface FilterEditorContainerProps {
  filter: FilterViewModel<string, unknown>
  testId?: string
}

/**
 * The value editor for one applied filter, chosen by filter type.
 */
export const FilterEditorContainer = observer(function FilterEditorContainer({
  filter,
  testId,
}: FilterEditorContainerProps) {
  return (
    <>
      {filter instanceof DateRangeFilter ? (
        <DateRangeFilterViewContainer
          filter={filter}
          testId={testId && `${testId}-date-range-filter`}
        />
      ) : filter instanceof InfiniteSelectFilter ? (
        <InfiniteSelectFilterViewContainer filter={filter} testId={testId} />
      ) : filter instanceof SelectFilter ? (
        <SelectFilterViewContainer
          filter={filter}
          testId={testId && `${testId}-select-filter`}
        />
      ) : filter instanceof SingleSelectFilter ? (
        <SingleSelectFilterViewContainer
          filter={filter}
          testId="single-select-filter"
        />
      ) : filter instanceof NumberRangeFilter ? (
        <NumberRangeFilterViewContainer
          filter={filter}
          unitName="price"
          currencyCode="USD"
          testId={testId && `${testId}-number-range-filter`}
        />
      ) : filter instanceof FlagFilter ? (
        <></>
      ) : (
        <>No filter found</>
      )}
    </>
  )
})

interface AvailableFiltersContainerProps<
  TFilters extends Record<string, unknown>,
> {
  vm: FiltersViewModel<TFilters>
  testId?: string
}

/**
 * The filters of a {@link FiltersViewModel} that can be added.
 */
export const AvailableFiltersContainer = observer(
  function AvailableFiltersContainer<TFilters extends Record<string, unknown>>({
    vm,
    testId,
  }: AvailableFiltersContainerProps<TFilters>) {
    return (
      <AvailableFilters>
        {vm.availableFilters
          .filter((f) => f.isVisible())
          .map((f) => (
            <AvailableFilterItem
              key={f.key}
              icon={f.icon!}
              title={f.title}
              description={f.current?.toString()}
              conflictTitle={f.conflict?.title}
              testId={testId && `${testId}-type-selector-${f.title}`}
              onPress={() =>
                f.current ? vm.editFilter(f.key) : vm.addFilter(f.key)
              }
            />
          ))}
      </AvailableFilters>
    )
  },
)
